import React, { useState } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Input,
  Card,
  Modal,
  message,
  Row,
  Col,
  Typography,
  Tooltip,
  Statistic,
  ConfigProvider
} from 'antd';
// Removed unused imports
import { useQuery, useMutation, useQueryClient } from 'react-query';
import dayjs from 'dayjs';
import { supplierService, Supplier, CreateSupplierData, UpdateSupplierData } from '../../services/supplierService';
import SupplierModal from '../../components/suppliers/SupplierModal';
import styles from './SuppliersPage.module.css';
import locale from 'antd/locale/vi_VN';

const { Search } = Input;
const { Text } = Typography;

const SuppliersPage: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | undefined>();


  const queryClient = useQueryClient();

  // Query danh sách nhà cung cấp
  const { data: suppliers = [], isLoading } = useQuery(
    ['suppliers', searchText],
    () => searchText ? supplierService.searchSuppliers(searchText) : supplierService.getSuppliers(),
    {
      staleTime: 5 * 60 * 1000, // 5 phút
    }
  );

  // Mutation tạo nhà cung cấp
  const createMutation = useMutation(
    (data: CreateSupplierData) => supplierService.createSupplier(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['suppliers']);
        message.success('Thêm nhà cung cấp thành công!');
        setModalVisible(false);
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Có lỗi xảy ra khi thêm nhà cung cấp!');
      }}
  );

  // Mutation cập nhật nhà cung cấp
  const updateMutation = useMutation(
    ({ id, data }: { id: number; data: UpdateSupplierData }) => 
      supplierService.updateSupplier(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['suppliers']);
        message.success('Cập nhật nhà cung cấp thành công!');
        setModalVisible(false);
        setSelectedSupplier(undefined);
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Có lỗi xảy ra khi cập nhật nhà cung cấp!');
      }}
  );

  // Mutation xóa nhà cung cấp
  const deleteMutation = useMutation(
    (id: number) => supplierService.deleteSupplier(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['suppliers']);
        message.success('Xóa nhà cung cấp thành công!');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Có lỗi xảy ra khi xóa nhà cung cấp!');
      }}
  );

  const handleAdd = () => {
    setSelectedSupplier(undefined);
    setModalVisible(true);
  };

  const handleEdit = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setModalVisible(true);
  };

  // Hàm handleDelete sử dụng Modal.confirm
  const handleDelete = (id: number) => {
    Modal.confirm({
      title: 'Bạn có chắc chắn muốn xóa nhà cung cấp này?',
      content: 'Hành động này không thể hoàn tác.',
      okText: 'Có, Xóa',
      okType: 'danger',
      cancelText: 'Không',
      onOk: () => {
        deleteMutation.mutate(id);
      },
      onCancel: () => {
        console.log('Hủy bỏ thao tác xóa.');
      }});
  };





  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  const clearFilters = () => {
    setSearchText('');
  };

  // Lọc dữ liệu theo filters
  const filteredSuppliers = suppliers;

  const getSummaryStats = () => {
    const totalSuppliers = filteredSuppliers.length;
    const activeSuppliers = filteredSuppliers.filter((s: Supplier) => s.is_active).length;
    const inactiveSuppliers = filteredSuppliers.filter((s: Supplier) => !s.is_active).length;
    const totalWithContact = filteredSuppliers.filter((s: Supplier) => s.phone || s.email).length;

    return (
      <div className={styles.statsContainer}>
        <Row gutter={[24, 16]} className={styles.statsRow}>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#3b82f6' }}>
                  <i className="fas fa-users" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Tổng nhà cung cấp"
                    value={totalSuppliers}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                  />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#10b981' }}>
                  <i className="fas fa-check-circle" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Đang hoạt động"
                    value={activeSuppliers}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                  />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#ef4444' }}>
                  <i className="fas fa-times-circle" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Không hoạt động"
                    value={inactiveSuppliers}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                  />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#f59e0b' }}>
                  <i className="fas fa-phone" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Có liên hệ"
                    value={totalWithContact}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                  />
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  const columns = [
    {
      title: 'Nhà cung cấp',
      key: 'supplier_info',
      width: 220,
      fixed: 'left' as const,
      sorter: (a: Supplier, b: Supplier) => a.name.localeCompare(b.name),
      render: (_: any, record: Supplier) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ 
            fontWeight: '600', 
            fontSize: '15px',
            color: '#1f2937',
            lineHeight: '1.2'
          }}>
            {record.name}
          </div>
          <div style={{ 
            fontSize: '12px', 
            color: '#6b7280',
            fontWeight: '400'
          }}>
            Mã: {record.code}
          </div>
        </div>
      )},
    {
      title: 'Liên hệ',
      key: 'contact_info',
      width: 200,
      render: (_: any, record: Supplier) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {record.contact_person && (
            <div style={{ 
              fontSize: '13px',
              fontWeight: '500',
              color: '#374151',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <i className="fas fa-user" style={{ color: '#6b7280', fontSize: '12px' }} />
              {record.contact_person}
            </div>
          )}
          {record.phone && (
            <div style={{ 
              fontSize: '13px',
              fontWeight: '500',
              color: '#374151',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <i className="fas fa-phone" style={{ color: '#6b7280', fontSize: '12px' }} />
              {record.phone}
            </div>
          )}
          {record.email && (
            <div style={{ 
              fontSize: '12px', 
              color: '#6b7280',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <i className="fas fa-envelope" style={{ color: '#6b7280', fontSize: '10px' }} />
              {record.email}
            </div>
          )}
        </div>
      )},
    {
      title: 'Thông tin',
      key: 'info',
      width: 160,
      render: (_: any, record: Supplier) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {record.address && (
            <div style={{ 
              fontSize: '12px', 
              color: '#6b7280',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '6px'
            }}>
              <i className="fas fa-map-marker-alt" style={{ color: '#6b7280', fontSize: '10px', marginTop: '2px' }} />
              <span style={{ 
                maxWidth: '140px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {record.address}
              </span>
            </div>
          )}
          <div style={{ 
            fontSize: '12px', 
            color: '#6b7280',
            fontWeight: '400'
          }}>
            Tạo: {dayjs(record.created_at).format('DD/MM/YYYY')}
          </div>
        </div>
      )},
    {
      title: 'Trạng thái',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 120,
      render: (isActive: boolean) => {
        const statusConfig = {
          active: { 
            color: '#059669', 
            bgColor: '#d1fae5',
            text: 'Hoạt động',
            icon: 'fas fa-check-circle'
          },
          inactive: { 
            color: '#dc2626', 
            bgColor: '#fee2e2',
            text: 'Không hoạt động',
            icon: 'fas fa-times-circle'
          }
        };
        const config = statusConfig[isActive ? 'active' : 'inactive'];
        return (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px',
            padding: '6px 12px',
            borderRadius: '20px',
            backgroundColor: config.bgColor,
            color: config.color,
            fontSize: '12px',
            fontWeight: '500',
            width: 'fit-content'
          }}>
            <i className={config.icon} style={{ fontSize: '10px' }} />
            {config.text}
          </div>
        );
      },
      filters: [
        { text: 'Hoạt động', value: 'active' },
        { text: 'Không hoạt động', value: 'inactive' }
      ],
      onFilter: (value: any, record: Supplier) => 
        (value === 'active' && record.is_active) || (value === 'inactive' && !record.is_active)},

    {
      title: 'Thao tác',
      key: 'actions',
      width: 100,
      fixed: 'right' as const,
      render: (_: any, record: Supplier) => (
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <Tooltip title="Sửa nhà cung cấp">
            <Button
              type="text"
              icon={<i className="fas fa-edit" />}
              onClick={() => handleEdit(record)}
              size="small"
              style={{ 
                color: '#f59e0b',
                border: 'none',
                boxShadow: 'none'
              }}
            />
          </Tooltip>
          <Tooltip title="Xóa nhà cung cấp">
            <Button
              type="text"
              icon={<i className="fas fa-trash" />}
              onClick={() => handleDelete(record.id)}
              size="small"
              style={{ 
                color: '#dc2626',
                border: 'none',
                boxShadow: 'none'
              }}
            />
          </Tooltip>
        </div>
      )},
  ];

  return (
    <ConfigProvider locale={locale}>
      <div className={styles.container}>
        {getSummaryStats()}

        <Card className={styles.tableCard}>
          <div className={styles.tableHeader}>
            <div className={styles.searchSection}>
            <Search
              placeholder="Tìm kiếm nhà cung cấp..."
              allowClear
                className={styles.searchInput}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              onSearch={handleSearch}
                prefix={<i className="fas fa-search" />}
                size="large"
                style={{ textAlign: 'left' }}
              />
            </div>
            <div className={styles.filterSection}>
              <Space size="middle" wrap>
                {searchText && (
              <Button
                type="link"
                icon={<i className="fas fa-filter" />}
                onClick={clearFilters}
                size="small"
              >
                Xóa bộ lọc
              </Button>
                )}
              </Space>
            </div>
            <div className={styles.actionButtons}>
              <Space size="middle">
                <Tooltip title="Làm mới dữ liệu">
                  <Button
                    icon={<i className="fas fa-sync-alt" />}
                    onClick={() => window.location.reload()}
                    className={styles.actionButton}
                  >
                    Làm mới
                  </Button>
                </Tooltip>
                <Tooltip title="Thêm nhà cung cấp mới">
                  <Button
                    type="primary"
                    icon={<i className="fas fa-plus" />}
                    onClick={handleAdd}
                    className={styles.primaryButton}
                  >
                    Thêm nhà cung cấp
                  </Button>
                </Tooltip>
              </Space>
            </div>
          </div>

        <Table
          columns={columns}
          dataSource={filteredSuppliers}
          rowKey="id"
          loading={isLoading}
            className={styles.suppliersTable}
            pagination={{
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} của ${total} nhà cung cấp`,
              className: styles.tablePagination,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
          size="middle"
            scroll={{ x: 700 }}
        />
        </Card>

      <SupplierModal
        visible={modalVisible}
        supplier={selectedSupplier}
        onSave={async (data: CreateSupplierData | UpdateSupplierData) => {
          try {
            if (selectedSupplier) {
              await updateMutation.mutateAsync({ id: selectedSupplier.id, data: data as UpdateSupplierData });
            } else {
              await createMutation.mutateAsync(data as CreateSupplierData);
            }
          } catch (error) {
            // Error handling is done in mutation
          }
        }}
        onCancel={() => {
          setModalVisible(false);
          setSelectedSupplier(undefined);
        }}
        loading={createMutation.isLoading || updateMutation.isLoading}
      />
    </div>
    </ConfigProvider>
  );
};

export default SuppliersPage; 